import dayjs from 'dayjs';
import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { getWeekDays } from '../../../../helpers/date-time';
import { useLang } from '../../../../hooks/use-lang';

export type DatePeriod = 'day' | 'week' | 'month' | 'year';

const format = 'DD.MM.YYYY';
const formatDate = (date: Date) => dayjs(date).format(format);

const isActiveWeek = (date: Date, cellDate: Date) => {
  const dayOfWeek = dayjs(date).isoWeekday();

  const currentDateWeekDates = Array.from({ length: 7 }, (_, i) =>
    dayjs(date)
      .subtract(dayOfWeek - 1, 'day')
      .add(i, 'day')
      .format(format),
  );
  return currentDateWeekDates.includes(formatDate(cellDate));
};

const isActiveMonth = (date: Date, cellDate: Date) => {
  const daysInMonth = dayjs(date).daysInMonth();

  const monthDates = Array.from({ length: daysInMonth }, (_, i) =>
    dayjs(date)
      .date(i + 1)
      .format(format),
  );

  return monthDates.includes(formatDate(cellDate));
};

function startOfLocalMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

/** `selectedDate` задаёт месяц при открытии и синхронизируется при смене значения снаружи. */
export const useDatePickerCalendar = (selectedDate?: Date) => {
  const selectedAtMs = selectedDate != null ? selectedDate.getTime() : null;

  const [displayedDates, setDisplayedDates] = useState(() =>
    selectedDate != null ? startOfLocalMonth(selectedDate) : new Date(),
  );

  const { t } = useLang();

  useLayoutEffect(() => {
    if (selectedAtMs == null) return;
    const d = new Date(selectedAtMs);
    setDisplayedDates(startOfLocalMonth(d));
  }, [selectedAtMs]);

  const displayedYear = displayedDates.getFullYear();
  const displayedMonth = displayedDates.getMonth();

  const handlePrevMonth = () => {
    setDisplayedDates(new Date(displayedYear, displayedMonth - 1, 1));
  };

  const handleNextMonth = () => {
    setDisplayedDates(new Date(displayedYear, displayedMonth + 1, 1));
  };

  const handlePrevYear = () => {
    setDisplayedDates(new Date(displayedYear - 1, displayedMonth, 1));
  };

  const handleNextYear = () => {
    setDisplayedDates(new Date(displayedYear + 1, displayedMonth, 1));
  };

  const { daysInMonth, daysInPrevMonth, emptyCellsAtEnd, firstDayOfMonth } = useMemo(() => {
    const firstDayOfMonth =
      (dayjs(new Date(displayedYear, displayedMonth, 1)).isoWeekday() - 1) % 7; // Смещение для начала недели с понедельника
    const daysInPrevMonth = dayjs(new Date(displayedYear, displayedMonth, 0)).date();
    const daysInMonth = dayjs(displayedDates).daysInMonth();
    const totalCells = firstDayOfMonth + daysInMonth;
    const emptyCellsAtEnd = (7 - (totalCells % 7)) % 7;
    return { firstDayOfMonth, daysInPrevMonth, daysInMonth, emptyCellsAtEnd };
  }, [displayedDates]);

  const currentDate = new Date();

  const daysInWeek = getWeekDays();

  const isCurrentDate = useCallback(
    (cellDate: Date, type: DatePeriod) => {
      if (type === 'week') return isActiveWeek(currentDate, cellDate);

      if (type === 'month') return isActiveMonth(currentDate, cellDate);

      return formatDate(currentDate) === formatDate(cellDate);
    },
    [currentDate],
  );

  const isSelectedDate = useCallback(
    (cellDate: Date, type: DatePeriod, selectedDate?: Date) => {
      if (!selectedDate) return false;

      if (type === 'week') return isActiveWeek(selectedDate, cellDate);

      if (type === 'month') return isActiveMonth(selectedDate, cellDate);

      return formatDate(selectedDate) === formatDate(cellDate);
    },
    [],
  );

  return {
    isCurrentDate,
    isSelectedDate,

    handleNextMonth,
    handleNextYear,
    handlePrevMonth,
    handlePrevYear,
    displayedYear,
    displayedMonth,
    displayedDates,
    firstDayOfMonth,
    daysInPrevMonth,
    daysInMonth,
    emptyCellsAtEnd,
    daysInWeek,

    t,
  };
};
